import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Form, Input, Button, Typography, Card, message, Space, Alert } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import Logo from './Logo';

const { Title, Text } = Typography;

export default function Login() {
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();
  const [form] = Form.useForm();

  // Check if the user was redirected from a successful verification
  useEffect(() => {
    // We will consider it verified if they were redirected to login from the auth/callback route
    // Note: this is a simple approach, you might want to add a query param for more precision
    const from = location.state?.from?.pathname;
    if (from === '/auth/callback') {
      setVerified(true);
      message.success('Uw e-mail is geverifieerd. U kunt nu inloggen.');
    }
  }, [location]);

  const handleSubmit = async (values: { email: string; password: string }) => {
    try {
      setLoading(true);
      await signIn(values.email, values.password);
      navigate('/');
    } catch (error: any) {
      console.error('Error during login:', error.message);
      
      // Handle email not confirmed error
      if (error.message && error.message.includes('Email not confirmed')) {
        message.error('Uw e-mail is nog niet bevestigd. Controleer uw inbox en bevestig uw e-mailadres.');
      } else {
        message.error('Inloggen mislukt. Controleer uw e-mailadres en wachtwoord.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      background: '#001350',
      backgroundImage: 'linear-gradient(135deg, #001350 0%, #002080 100%)',
    }}>
      <Card 
        style={{ 
          width: '100%', 
          maxWidth: 400,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          borderRadius: '8px',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Logo height={48} darkMode={true} />
          <Title level={3} style={{ marginTop: 24, color: '#001350' }}>Inloggen</Title>
        </div>

        {verified && (
          <Alert
            message="E-mail verificatie geslaagd"
            description="Uw e-mail is geverifieerd. U kunt nu inloggen met uw e-mailadres en wachtwoord."
            type="success"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Voer uw e-mailadres in' },
              { type: 'email', message: 'Voer een geldig e-mailadres in' }
            ]}
          >
            <Input 
              prefix={<UserOutlined style={{ color: '#001350' }} />}
              placeholder="E-mailadres"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: 'Voer uw wachtwoord in' }]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#001350' }} />}
              placeholder="Wachtwoord"
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              block
              size="large"
              style={{
                backgroundColor: '#FF5d00',
                borderColor: '#FF5d00',
                height: '45px',
                fontSize: '16px',
              }}
            >
              Inloggen
            </Button>
          </Form.Item>
        </Form>

        <Space direction="vertical" size="small" style={{ width: '100%', textAlign: 'center' }}>
          <Text>
            Nog geen account?{' '}
            <Link to="/register" style={{ color: '#FF5d00', fontWeight: 600 }}>
              Registreer nu
            </Link>
          </Text>
          <Link to="/forgot-password" style={{ color: '#001350' }}>
            Wachtwoord vergeten?
          </Link>
        </Space>
      </Card>
    </div>
  );
}
